import CalendarIcon from "@redotech/redo-icons/calendar-date.svg";
import { getDateString } from "@redotech/util/date-format";
import { memo, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { TextSizeValue } from "../../theme/typography";
import {
  RedoButton,
  RedoButtonHierarchy,
  RedoButtonSize,
} from "../buttons/redo-button";
import { RedoDateRangePickerDropdown } from "./redo-date-range-picker-dropdown";

export interface RedoDateRangePickerProps {
  "data-dd-action-name"?: string;
  dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined;
  setDateRange: (
    dateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined,
  ) => void;
  size?: RedoButtonSize;
  hierarchy?: RedoButtonHierarchy;
  /** Render only the calendar icon (the selected range still shows in the dropdown). */
  iconOnly?: boolean;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  labelText?: string;
  allowNoDateOption?: boolean;
  dateStringOptions?: Pick<
    Intl.DateTimeFormatOptions,
    "year" | "month" | "day"
  >;
  dropdownStyle?: React.CSSProperties;
  popperModifiers?: any[];
  placement?: "bottom-start" | "top-start";
  buttonContentClassName?: string;
  "data-testid"?: string;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

const dateRangePickerButtonSizeToLabelSize: Record<
  RedoButtonSize,
  TextSizeValue
> = {
  xs: "xs",
  sm: "xs",
  md: "sm",
  lg: "sm",
  xl: "sm",
  "2xl": "sm",
  "3xl": "sm",
};

export const RedoDateRangePicker = memo(function RedoDateRangePicker({
  dateRange,
  setDateRange,
  size = "lg",
  hierarchy = "secondary",
  iconOnly = false,
  minDate,
  maxDate,
  labelText,
  allowNoDateOption = false,
  dateStringOptions = {},
  dropdownStyle,
  popperModifiers,
  placement,
  buttonContentClassName,
  "data-testid": dataTestId,
  open: controlledOpen,
  setOpen: controlledSetOpen,
  "data-dd-action-name": actionName,
}: RedoDateRangePickerProps) {
  const buttonDisplay = dateRange
    ? `${getDateString(dateRange[0], dateStringOptions)} - ${getDateString(dateRange[1], dateStringOptions)}`
    : "Select dates";

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const [internalOpen, setInternalOpen] = useState(false);
  const pickerOpen = controlledOpen ?? internalOpen;
  const setPickerOpen = controlledSetOpen ?? setInternalOpen;

  const labelSize: TextSizeValue = dateRangePickerButtonSizeToLabelSize[size];

  return (
    <>
      <Flex
        data-arbiter="RedoDateRangePicker"
        data-dd-action-name={actionName}
        dir="column"
        gap="xs"
        justify="flex-start"
      >
        {labelText && (
          <Text fontSize={labelSize} fontWeight="medium" textColor="secondary">
            {labelText}
          </Text>
        )}
        <RedoButton
          aria-label={iconOnly ? buttonDisplay : undefined}
          buttonContentClassName={buttonContentClassName}
          data-dd-action-name={actionName}
          data-testid={dataTestId}
          hierarchy={hierarchy}
          IconLeading={CalendarIcon}
          onClick={(e) => {
            e.stopPropagation();
            setPickerOpen(!pickerOpen);
          }}
          pressed={iconOnly && dateRange != null}
          ref={setDropdownAnchor}
          size={size}
          text={iconOnly ? undefined : buttonDisplay}
        />
      </Flex>
      <RedoDateRangePickerDropdown
        allowNoDateOption={allowNoDateOption}
        anchorEl={dropdownAnchor}
        data-dd-action-name={actionName}
        dateRange={dateRange}
        maxDate={maxDate}
        minDate={minDate}
        onClose={() => setPickerOpen(false)}
        open={pickerOpen}
        placement={placement}
        popperModifiers={popperModifiers}
        setDateRange={setDateRange}
        style={dropdownStyle}
      />
    </>
  );
});
